import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/common/Layout';
import { useAppData } from '../context/AppDataContext';
import { useAuth } from '../context/AuthContext';
import { MtdOrdersRow } from '../types';
import { orderReportService } from '../services/orderReportService';
import {
  DateRangePreset,
  formatDate,
  getDateRangeForPreset,
} from '../utils/dateUtils';

const formatQuantity = (value: number): string =>
  Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const MtdOrdersTable: React.FC<{
  title: string;
  nameHeader: string;
  rows: MtdOrdersRow[];
  loading: boolean;
  totals: { orders_placed: number; quantity: number };
}> = ({ title, nameHeader, rows, loading, totals }) => (
  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-100">
      <h2 className="text-base font-bold text-gray-900">{title}</h2>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
              {nameHeader}
            </th>
            <th className="px-3 py-2 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
              Orders placed
            </th>
            <th className="px-3 py-2 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
              Quantity (tons)
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {loading ? (
            <tr>
              <td colSpan={3} className="px-3 py-6 text-center text-sm text-gray-500">
                Loading...
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={3} className="px-3 py-6 text-center text-sm text-gray-500">
                No orders placed in this period.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 text-xs text-gray-700">{r.name || '-'}</td>
                <td className="px-3 py-2 text-xs text-gray-700 text-right">{r.orders_placed}</td>
                <td className="px-3 py-2 text-xs text-gray-700 text-right">{formatQuantity(r.quantity)}</td>
              </tr>
            ))
          )}
        </tbody>
        {!loading && rows.length > 0 && (
          <tfoot className="bg-gray-50">
            <tr>
              <td className="px-3 py-2 text-xs font-bold text-gray-800">Total</td>
              <td className="px-3 py-2 text-xs font-bold text-gray-800 text-right">{totals.orders_placed}</td>
              <td className="px-3 py-2 text-xs font-bold text-gray-800 text-right">
                {formatQuantity(totals.quantity)}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  </div>
);

const MtdOrdersReport: React.FC = () => {
  const { user } = useAuth();
  const { users, refreshUsersForDropdown } = useAppData();
  const monthRange = getDateRangeForPreset('this_month');
  const [byBrand, setByBrand] = useState<MtdOrdersRow[]>([]);
  const [bySalesOfficer, setBySalesOfficer] = useState<MtdOrdersRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [salesOfficerId, setSalesOfficerId] = useState<number | ''>('');
  const [preset, setPreset] = useState<DateRangePreset>('this_month');
  const [dateFrom, setDateFrom] = useState(monthRange.from);
  const [dateTo, setDateTo] = useState(monthRange.to);

  useEffect(() => {
    refreshUsersForDropdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canViewReports =
    user?.role === 'org_admin' || user?.role === 'manager' || user?.role === 'executive';

  const loadReport = async (nextSalesOfficerId: number | '', from: string, to: string) => {
    try {
      setLoading(true);
      setError('');
      const result = await orderReportService.getMtdOrders({
        sales_officer_id:
          user?.role === 'executive' && user?.id != null
            ? user.id
            : nextSalesOfficerId === ''
              ? undefined
              : nextSalesOfficerId,
        date_from: from || undefined,
        date_to: to || undefined,
      });
      setByBrand(result.by_brand || []);
      setBySalesOfficer(result.by_sales_officer || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canViewReports) {
      return;
    }
    loadReport(salesOfficerId, dateFrom, dateTo);
    // Initial load and when role/user identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.id]);

  const applyPreset = (next: DateRangePreset) => {
    setPreset(next);
    if (next === 'custom') {
      return;
    }
    const range = getDateRangeForPreset(next);
    setDateFrom(range.from);
    setDateTo(range.to);
    loadReport(salesOfficerId, range.from, range.to);
  };

  const handleSearch = () => {
    loadReport(salesOfficerId, dateFrom, dateTo);
  };

  const handleClear = () => {
    const range = getDateRangeForPreset('this_month');
    setSalesOfficerId('');
    setPreset('this_month');
    setDateFrom(range.from);
    setDateTo(range.to);
    loadReport('', range.from, range.to);
  };

  const brandTotals = useMemo(
    () =>
      byBrand.reduce(
        (acc, r) => ({
          orders_placed: acc.orders_placed + r.orders_placed,
          quantity: acc.quantity + Number(r.quantity || 0),
        }),
        { orders_placed: 0, quantity: 0 }
      ),
    [byBrand]
  );

  const soTotals = useMemo(
    () =>
      bySalesOfficer.reduce(
        (acc, r) => ({
          orders_placed: acc.orders_placed + r.orders_placed,
          quantity: acc.quantity + Number(r.quantity || 0),
        }),
        { orders_placed: 0, quantity: 0 }
      ),
    [bySalesOfficer]
  );

  if (!canViewReports) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
          You do not have permission to view this report.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">MTD Orders Placed</h1>
          <p className="text-xs text-gray-500">
            {formatDate(dateFrom)} – {formatDate(dateTo)}
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
            {user?.role !== 'executive' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sales Officer</label>
                <select
                  value={salesOfficerId}
                  onChange={(e) => setSalesOfficerId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">All SOs</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <select
                value={preset}
                onChange={(e) => applyPreset(e.target.value as DateRangePreset)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="this_month">This month</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last_7">Last 7 days</option>
                <option value="last_30">Last 30 days</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            {preset === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </>
            )}
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="bg-yellow-500 text-black-950 px-4 py-2 rounded-md hover:bg-yellow-600 text-sm font-bold"
              >
                Search
              </button>
              <button
                onClick={handleClear}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 text-sm font-medium"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">{error}</div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <MtdOrdersTable
            title="Brand-wise"
            nameHeader="Brand"
            rows={byBrand}
            loading={loading}
            totals={brandTotals}
          />
          <MtdOrdersTable
            title="SO-wise"
            nameHeader="Sales Officer"
            rows={bySalesOfficer}
            loading={loading}
            totals={soTotals}
          />
        </div>
      </div>
    </Layout>
  );
};

export default MtdOrdersReport;
